import { createContext, ReactNode, useContext, useEffect, useState } from "react"
import { InstantSearch } from "react-instantsearch"
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter"
import { SearchPostHogClient, type SearchPostHogProperties } from "../../search-standalone/services/SearchPostHogClient"
import { TokenManager } from "../../search-standalone/services/TokenManager"

interface SearchProviderProps {
	children: ReactNode
	organizationId: string
	userId?: string
	reverseDemoExternalSessionId?: string
	reverseDemoStoreSlug?: string
	reverseDemoStoreUrl?: string
}

interface CollectionInfo {
	name: string
	defaultSearchMethod: "keyword" | "semantic" | "hybrid" | "natural_language"
	searchCapabilities: {
		keyword: boolean
		semantic: boolean
		hybrid: boolean
		naturalLanguage: boolean
	}
}

interface SearchClientContextValue {
	searchClient: any
	selectedCollections: CollectionInfo[]
	refreshCollections: () => Promise<void>
	analyticsContext: SearchAnalyticsContext
	buildAnalyticsProperties: (properties?: SearchPostHogProperties) => SearchPostHogProperties
}

interface SearchAnalyticsContext {
	organizationId: string
	externalSessionId?: string
	storeSlug?: string
	storeUrl?: string
}

const SearchClientContext = createContext<SearchClientContextValue | null>(null)

export const useSearchClient = () => {
	const context = useContext(SearchClientContext)
	if (!context) {
		throw new Error("useSearchClient must be used within SearchProvider")
	}
	return context
}

interface TypesenseConfig {
	host: string
	port: number
	protocol: string
	apiKey: string
}

/**
 * SearchProvider wraps the InstantSearch context with Typesense adapter
 * Handles configuration fetching, adapter initialization, and multi-collection search
 */
export const SearchProvider = ({
	children,
	organizationId,
	userId,
	reverseDemoExternalSessionId,
	reverseDemoStoreSlug,
	reverseDemoStoreUrl,
}: SearchProviderProps) => {
	// Use useMemo to create a stable searchClient reference
	const [searchClient, setSearchClient] = useState<any>(null)
	const [selectedCollections, setSelectedCollections] = useState<CollectionInfo[]>([])
	const [error, setError] = useState<string | null>(null)
	const [isInitialized, setIsInitialized] = useState(false)
	const searchPostHog = SearchPostHogClient.getInstance()
	const analyticsContext: SearchAnalyticsContext = {
		organizationId,
		externalSessionId: reverseDemoExternalSessionId,
		storeSlug: reverseDemoStoreSlug,
		storeUrl: reverseDemoStoreUrl,
	}
	const buildAnalyticsProperties = (properties: SearchPostHogProperties = {}): SearchPostHogProperties => ({
		...searchPostHog.buildCorrelationProperties({
			organizationId,
			externalSessionId: reverseDemoExternalSessionId,
			storeSlug: reverseDemoStoreSlug,
			storeUrl: reverseDemoStoreUrl,
		}),
		...properties,
	})

	// Fetch selected collections for this organization with their configurations
	const fetchSelectedCollections = async (): Promise<CollectionInfo[]> => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const url = new URL("/api/v1/search/collections/selected", backendUrl || window.location.origin)
			url.searchParams.set("organizationId", organizationId)

			// GET /collections/selected is protected — requires a Bearer token
			const token = await TokenManager.getInstance().getBackendToken()
			const response = await fetch(url.toString(), {
				headers: { Authorization: `Bearer ${token}` },
			})
			if (!response.ok) {
				console.warn("[SearchProvider] Failed to fetch selected collections, using default")
				searchPostHog.capture("search_selected_collections_loaded", {
					...buildAnalyticsProperties(),
					collection_count: 1,
					collection_names: ["products"],
					used_default_collections: true,
					load_status: "fallback_response_not_ok",
					response_status: response.status,
				})
				return [
					{
						name: "products",
						defaultSearchMethod: "keyword",
						searchCapabilities: { keyword: true, semantic: false, hybrid: false, naturalLanguage: false },
					},
				]
			}

			const data = await response.json()
			const collections: CollectionInfo[] = data.selectedCollections || [
				{
					name: "products",
					defaultSearchMethod: "keyword",
					searchCapabilities: { keyword: true, semantic: false, hybrid: false, naturalLanguage: false },
				},
			]
			console.log("[SearchProvider] Loaded selected collections:", collections)
			searchPostHog.capture("search_selected_collections_loaded", {
				...buildAnalyticsProperties(),
				collection_count: collections.length,
				collection_names: collections.map((collection) => collection.name),
				used_default_collections: false,
				load_status: "success",
			})
			return collections
		} catch (err) {
			console.warn("[SearchProvider] Error fetching selected collections:", err)
			searchPostHog.capture("search_selected_collections_loaded", {
				...buildAnalyticsProperties(),
				...searchPostHog.normalizeError(err),
				collection_count: 1,
				collection_names: ["products"],
				used_default_collections: true,
				load_status: "fallback_exception",
			})
			return [
				{
					name: "products",
					defaultSearchMethod: "keyword",
					searchCapabilities: { keyword: true, semantic: false, hybrid: false, naturalLanguage: false },
				},
			]
		}
	}

	const initializeSearchClient = async () => {
		try {
			// Fetch Typesense configuration from backend
			const configUrl = new URL("/api/v1/search/typesense/config", window.location.origin)
			configUrl.searchParams.set("organizationId", organizationId)
			if (userId) {
				configUrl.searchParams.set("userId", userId)
			}

			const response = await fetch(configUrl.toString())
			if (!response.ok) {
				throw new Error(`Failed to fetch Typesense config: ${response.statusText}`)
			}

			const config: TypesenseConfig = await response.json()

			// Fetch selected collections
			const collections = await fetchSelectedCollections()
			setSelectedCollections(collections)

			// Determine the primary collection (first selected or default to "products")
			const primaryCollection = collections.length > 0 ? collections[0] : "products"

			// Initialize Typesense InstantSearch adapter
			// IMPORTANT: Configure to use backend proxy instead of direct Typesense connection
			const backendUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin

			const adapter = new TypesenseInstantSearchAdapter({
				server: {
					apiKey: config.apiKey,
					nodes: [
						{
							// Use backend proxy endpoint instead of direct Typesense
							host: new URL(backendUrl).hostname,
							port: new URL(backendUrl).port
								? parseInt(new URL(backendUrl).port, 10)
								: new URL(backendUrl).protocol === "https:"
									? 443
									: 80,
							protocol: new URL(backendUrl).protocol.replace(":", ""),
							path: "/api/v1/search/typesense",
						},
					],
				},
				// Additional parameters to be sent with every search request
				additionalSearchParameters: {
					// DO NOT hardcode query_by - let backend determine based on collection config
					// Multi-tenant filtering - only show products for this organization
					filter_by: `organizationId:=${organizationId}`,
					// Faceting configuration - include price for range filtering
					facet_by: "category,brand,inStock,price",
					max_facet_values: 50,
					// Sorting
					sort_by: "_text_match:desc,rating:desc",
					// Typo tolerance
					num_typos: 2,
					typo_tokens_threshold: 1,
					...(reverseDemoExternalSessionId ? { external_session_id: reverseDemoExternalSessionId } : {}),
					...(reverseDemoStoreSlug ? { store_slug: reverseDemoStoreSlug } : {}),
					...(reverseDemoStoreUrl ? { store_url: reverseDemoStoreUrl } : {}),
				},
				// Specify collection-specific search parameters
				collectionSpecificSearchParameters: {
					// Only specify query_by for special collections like query_suggestions
					query_suggestions: {
						query_by: "q",
					},
					// For other collections, let backend determine query_by based on config
				},
			})

			setSearchClient(adapter.searchClient)
			setError(null)
			setIsInitialized(true)
			searchPostHog.capture("search_client_initialized", {
				...buildAnalyticsProperties(),
				collection_count: collections.length,
				collection_names: collections.map((collection) => collection.name),
				primary_collection_name: typeof primaryCollection === "string" ? primaryCollection : primaryCollection.name,
				backend_url_host: new URL(backendUrl).hostname,
				backend_protocol: new URL(backendUrl).protocol.replace(":", ""),
				backend_has_custom_port: Boolean(new URL(backendUrl).port),
			})
		} catch (err) {
			console.error("[SearchProvider] Failed to initialize:", err)
			searchPostHog.capture("search_client_initialization_failed", {
				...buildAnalyticsProperties(),
				...searchPostHog.normalizeError(err),
			})
			setError(err instanceof Error ? err.message : "Failed to initialize search")
		}
	}

	useEffect(() => {
		// Only initialize once per organizationId/userId combination
		if (!isInitialized) {
			initializeSearchClient()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organizationId, userId, isInitialized, reverseDemoExternalSessionId, reverseDemoStoreSlug, reverseDemoStoreUrl])

	// Show error state
	if (error) {
		return (
			<div className="flex items-center justify-center p-8 text-center">
				<div className="max-w-md">
					<p className="text-destructive font-medium mb-2">Search Initialization Failed</p>
					<p className="text-sm text-muted-foreground">{error}</p>
				</div>
			</div>
		)
	}

	// Show loading state while initializing
	if (!searchClient) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="flex items-center gap-2 text-muted-foreground">
					<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
					<span className="text-sm">Initializing search...</span>
				</div>
			</div>
		)
	}

	// Determine the primary collection for InstantSearch indexName
	const primaryCollectionName = selectedCollections.length > 0 ? selectedCollections[0].name : "products"

	const contextValue: SearchClientContextValue = {
		searchClient,
		selectedCollections,
		refreshCollections: async () => {
			const collections = await fetchSelectedCollections()
			setSelectedCollections(collections)
		},
		analyticsContext,
		buildAnalyticsProperties,
	}

	return (
		<SearchClientContext.Provider value={contextValue}>
			<InstantSearch indexName={primaryCollectionName} searchClient={searchClient}>
				{children}
			</InstantSearch>
		</SearchClientContext.Provider>
	)
}
